import { Close, Edit, Link as LinkIcon } from '@mui/icons-material'
import { Box, IconButton, Popover, Tooltip, Typography } from '@mui/material'
import { type ChapterSectionDependency } from '@shared/chapterSectionDependencies'
import React, { useState } from 'react'

import { DEPENDENCY_KINDS, DEPENDENCY_KIND_COPY } from './dependencyKindLabels'

interface DependencyChipsProps {
  outgoing: ChapterSectionDependency[]
  incoming: ChapterSectionDependency[]
  violations: Set<string>
  bookDirection: 'ltr' | 'rtl'
  bookFontFamily?: string
  labelByKey: (key: string) => string
  onRemove: (depId: string) => void
  onEdit: (dep: ChapterSectionDependency) => void
}

/**
 * A single compact chip on the section row showing the total dependency count.
 * Click opens a popover that groups deps under semantic headings (must come
 * before / must come after) regardless of layout direction.
 */
export default function DependencyChips({
  outgoing,
  incoming,
  violations,
  bookDirection,
  bookFontFamily,
  labelByKey,
  onRemove,
  onEdit,
}: DependencyChipsProps) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)

  const total = outgoing.length + incoming.length
  if (total === 0) {
    return null
  }

  const hasViolation = outgoing.some((d) => violations.has(d.id)) || incoming.some((d) => violations.has(d.id))

  const all = [...incoming, ...outgoing]
  const tooltip = [
    ...DEPENDENCY_KINDS.map((kind) => {
      const n = all.filter((d) => d.kind === kind).length
      return n > 0 ? `${n} · ${DEPENDENCY_KIND_COPY[kind].name}` : null
    }).filter((s): s is string => s !== null),
    ...(hasViolation ? ['⚠ יש הפרה'] : []),
  ].join(' · ')

  return (
    <>
      <Tooltip title={tooltip} placement="top" arrow>
        <Box
          component="button"
          onClick={(e) => {
            e.stopPropagation()
            setAnchor(e.currentTarget)
          }}
          sx={chipSx(hasViolation)}
        >
          <LinkIcon sx={{ fontSize: 12 }} />
          {total}
        </Box>
      </Tooltip>
      <Popover
        open={anchor !== null}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        slotProps={{
          paper: {
            dir: bookDirection,
            elevation: 8,
            sx: {
              p: 1.5,
              minWidth: 280,
              maxWidth: 380,
              borderRadius: 2,
              backgroundImage: 'none',
            },
          },
        }}
      >
        {/* Grouped by kind first, then by direction — a seeding thread and a hard
            ordering constraint read as different things and shouldn't be interleaved. */}
        {DEPENDENCY_KINDS.flatMap((kind) => {
          const groups: Array<{
            title: string
            deps: ChapterSectionDependency[]
            otherKeyOf: (d: ChapterSectionDependency) => string
          }> = [
            {
              title: DEPENDENCY_KIND_COPY[kind].incoming,
              deps: incoming.filter((d) => d.kind === kind),
              otherKeyOf: (d: ChapterSectionDependency) => d.fromKey,
            },
            {
              title: DEPENDENCY_KIND_COPY[kind].outgoing,
              deps: outgoing.filter((d) => d.kind === kind),
              otherKeyOf: (d: ChapterSectionDependency) => d.toKey,
            },
          ]
          return groups
            .filter((g) => g.deps.length > 0)
            .map((g) => (
              <Box key={`${kind}-${g.title}`} sx={{ '& + &': { mt: 1 }, mb: 1, '&:last-of-type': { mb: 0 } }}>
                <DepGroup
                  title={g.title}
                  deps={g.deps}
                  otherKeyOf={g.otherKeyOf}
                  violations={violations}
                  bookDirection={bookDirection}
                  bookFontFamily={bookFontFamily}
                  labelByKey={labelByKey}
                  onRemove={(id) => {
                    onRemove(id)
                    if (total === 1) {
                      setAnchor(null)
                    }
                  }}
                  onEdit={(d) => {
                    setAnchor(null)
                    onEdit(d)
                  }}
                />
              </Box>
            ))
        })}
      </Popover>
    </>
  )
}

interface DepGroupProps {
  title: string
  deps: ChapterSectionDependency[]
  otherKeyOf: (d: ChapterSectionDependency) => string
  violations: Set<string>
  bookDirection: 'ltr' | 'rtl'
  bookFontFamily?: string
  labelByKey: (key: string) => string
  onRemove: (depId: string) => void
  onEdit: (dep: ChapterSectionDependency) => void
}

function DepGroup({
  title,
  deps,
  otherKeyOf,
  violations,
  bookDirection,
  bookFontFamily,
  labelByKey,
  onRemove,
  onEdit,
}: DepGroupProps) {
  return (
    <Box>
      <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mb: 0.75 }}>{title}</Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        {deps.map((dep) => {
          const otherKey = otherKeyOf(dep)
          const isViolation = violations.has(dep.id)
          return (
            <Box
              key={dep.id}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 0.5,
                p: 0.75,
                borderRadius: 1,
                border: '1px solid',
                borderColor: isViolation ? 'error.main' : 'divider',
                bgcolor: isViolation ? 'error.50' : 'transparent',
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  sx={{
                    fontFamily: bookFontFamily || 'SBL Hebrew',
                    fontSize: '0.85rem',
                    textAlign: bookDirection === 'rtl' ? 'right' : 'left',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {labelByKey(otherKey)}
                </Typography>
                {dep.reason && (
                  <Typography
                    sx={{
                      fontSize: '0.75rem',
                      color: 'text.secondary',
                      mt: 0.25,
                      textAlign: bookDirection === 'rtl' ? 'right' : 'left',
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {dep.reason}
                  </Typography>
                )}
                {isViolation && (
                  <Typography sx={{ fontSize: '0.7rem', color: 'error.main', mt: 0.25 }}>
                    ⚠ הסדר הנוכחי מפר את התלות
                  </Typography>
                )}
              </Box>
              <IconButton size="small" onClick={() => onEdit(dep)} title="ערוך הסבר" sx={{ p: 0.25 }}>
                <Edit sx={{ fontSize: 14 }} />
              </IconButton>
              <IconButton size="small" onClick={() => onRemove(dep.id)} title="מחק תלות" sx={{ p: 0.25 }}>
                <Close sx={{ fontSize: 14 }} />
              </IconButton>
            </Box>
          )
        })}
      </Box>
    </Box>
  )
}

function chipSx(isViolation: boolean) {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.25,
    minWidth: 28,
    height: 18,
    px: 0.5,
    border: '1px solid',
    borderColor: isViolation ? 'error.main' : 'divider',
    borderRadius: 1,
    bgcolor: isViolation ? 'error.50' : 'action.hover',
    color: isViolation ? 'error.main' : 'text.secondary',
    fontSize: '0.7rem',
    fontWeight: 600,
    cursor: 'pointer',
    background: 'transparent',
    '&:hover': {
      bgcolor: isViolation ? 'error.100' : 'action.selected',
    },
  } as const
}
